import { useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    addMessage,
    setIsLoading,
    updateMessage,
    setError,
    setReasoning,
    Message,
    initialiseMessageThread,
    setIsLoadingChatHistory,
    setGlobalSuggestedQuestions,
    setAskAicaRightPanelMode,
    ReasoningStep,
    resetReasoning,
    setIsClearingChat,
} from './actions';
import { clearMemory, getChatHistory, streamAskAicaChat } from './apiHelper';

export const useAskAica = () => {
    const streamStartedCallbackRef = useRef<((value: boolean) => void) | null>(null);
    const dispatch = useAppDispatch();
    const user_id = useAppSelector((state) => state.user.userData.userId);
    const params = useParams();
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const { messages, reasoning, isLoading, error, uiState, pagination, isLoadingChatHistory } = useAppSelector(
        (state) => state.dashboard.askAicaReducer,
    );
    const { selectedSources } = uiState;
    const org_id = params.id ? params.id : investorOrgId;
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const fetchChatHistory = useCallback(
        (isInitialFetch = false) => {
            dispatch(setIsLoadingChatHistory(true));
            getChatHistory(user_id, org_id, pagination.page, pagination.pageSize)
                .then((data) => {
                    let messageThread: Message[] = [];
                    if (data && Array.isArray(data.items)) {
                        data.items.reverse().forEach((item: { user_message?: Message; assistant_message?: Message }) => {
                            if (item.assistant_message)
                                messageThread.push({
                                    id: item.assistant_message.id,
                                    type: item.assistant_message.type,
                                    timestamp: new Date(item.assistant_message.created_at as string),
                                    content: item.assistant_message.input_text as string,
                                    status: item.assistant_message.status as string,
                                });
                            if (item.user_message)
                                messageThread.push({
                                    id: item.user_message.id,
                                    type: item.user_message.type,
                                    timestamp: new Date(item.user_message.created_at as string),
                                    content: item.user_message.input_text as string,
                                    status: item.user_message.status as string,
                                });
                        });

                        // If there are suggestions from chat history, set them as global suggestions
                        if (data.suggestions && Array.isArray(data.suggestions) && data.suggestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(data.suggestions));
                        }

                        dispatch(initialiseMessageThread(messageThread));
                    }
                })
                .catch((err) => {
                    console.error('Error fetching chat history:', err);
                    toast.error('Failed to fetch chat history');
                    dispatch(setIsLoadingChatHistory(false));
                });
        },
        [user_id, org_id, pagination],
    );

    // Function to send a message and get a streaming response
    const sendMessage = useCallback(
        (content: string) => {
            if (!content.trim() || isLoading) return;

            // Reset stream started state when sending a new message
            if (streamStartedCallbackRef.current) {
                streamStartedCallbackRef.current(false);
            }

            // Create and add user message
            const userMessage = {
                id: Date.now(),
                type: 'user' as const,
                content,
                timestamp: new Date(),
                sources: selectedSources,
            };
            dispatch(addMessage(userMessage));

            // Create placeholder for AI response
            const aiMessageId = Date.now() + 1;
            const aiMessage = {
                id: aiMessageId,
                type: 'assistant' as const,
                content: '',
                timestamp: new Date(),
            };
            dispatch(addMessage(aiMessage));
            dispatch(setIsLoading(true));
            dispatch(resetReasoning());
            dispatch(setAskAicaRightPanelMode('reasoning'));

            // Format messages for API
            const apiMessages = [...messages]
                .reverse()
                .concat(userMessage)
                .slice(-3)
                .map((msg) => ({
                    role: msg.type === 'user' ? 'user' : 'assistant',
                    content: msg.content,
                }));

            // Abort controller for cleaning up the stream
            let abortStream: () => void;

            try {
                // Stream the response
                abortStream = streamAskAicaChat(
                    apiMessages,
                    selectedSources,
                    user_id,
                    org_id,
                    // On each chunk
                    (chunk, suggestedQuestions, reasoning, hasNewReasoning, isFirst, metadata) => {
                        // Update the message content
                        dispatch(updateMessage(aiMessageId, chunk, metadata || {}));

                        // If this is actual content (not just a step_type update), notify that streaming has started
                        if (chunk && streamStartedCallbackRef.current) {
                            streamStartedCallbackRef.current(true);
                        }

                        // Handle suggested questions if present
                        if (suggestedQuestions && suggestedQuestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(suggestedQuestions));
                        }

                        // Handle reasoning if present
                        dispatch(setReasoning(aiMessageId, reasoning as ReasoningStep[], hasNewReasoning));
                    },
                    // On complete
                    (response) => {
                        dispatch(setIsLoading(false));
                        if (response.metadata) {
                            dispatch(updateMessage(aiMessageId, '', response.metadata));
                        }

                        // Set final suggested questions if any
                        if (response.suggestedQuestions && response.suggestedQuestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(response.suggestedQuestions));
                        }

                        // Set final reasoning if any
                        dispatch(setReasoning(aiMessageId, response.reasoning as ReasoningStep[], response.hasNewReasoning));

                        // Set right panel mode to suggestions after 2 seconds
                        setTimeout(() => {
                            dispatch(setAskAicaRightPanelMode('suggestions'));
                        }, 2000);
                    },
                    // On error
                    (error) => {
                        console.error('Error in streamAskAicaChat:', error);
                        dispatch(setIsLoading(false));
                        dispatch(setError(error.message || 'Failed to get response'));
                    },
                );
            } catch (error: any) {
                dispatch(setIsLoading(false));
                dispatch(setError(error.message || 'Failed to send message'));
            }

            // Return cleanup function
            return () => {
                if (abortStream) abortStream();
            };
        },
        [dispatch, isLoading, messages, selectedSources],
    );

    const clearChatMemory = useCallback(() => {
        dispatch(setIsClearingChat(true));
        clearMemory(user_id, org_id)
            .then((data) => {
                toast.success('Chat memory cleared successfully');
                dispatch(setIsClearingChat(false));
            })
            .catch((err) => {
                console.error('Error clearing chat:', err);
                dispatch(setIsClearingChat(false));
                toast.error('Failed to clear chat memory');
            });
    }, [dispatch, user_id, org_id]);

    const registerStreamStartedCallback = (callback: (value: boolean) => void) => {
        streamStartedCallbackRef.current = callback;
    };

    return {
        messages,
        reasoning,
        isLoading,
        error,
        sendMessage,
        clearChatMemory,
        registerStreamStartedCallback,
        isLoadingChatHistory,
        messagesEndRef,
        fetchChatHistory,
        hasMoreData: pagination.hasMoreData,
    };
};

export default useAskAica;
